import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "./utils";

/**
 * Базовый контейнер для самостоятельных блоков (§11 «Карточки»).
 * 12px радиус, тонкая тень, padding по умолчанию 24px (компактный — 16px).
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  density?: "compact" | "comfortable";
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, density = "comfortable", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-xl border border-base-200 bg-surface shadow-sm",
        density === "compact" ? "p-4" : "p-6",
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

// Omit the native HTML `title` (string) attribute so our ReactNode-typed
// `title` prop doesn't conflict under exactOptionalPropertyTypes.
export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, actions, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("mb-3 flex items-start justify-between gap-3", className)}
      {...props}
    >
      <div className="min-w-0 flex-1">
        <div className="font-display text-lg font-semibold text-base-900">{title}</div>
        {description && (
          <div className="mt-1 text-sm text-base-600">{description}</div>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  ),
);
CardHeader.displayName = "CardHeader";

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("text-base-700", className)} {...props} />
  ),
);
CardBody.displayName = "CardBody";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "mt-4 flex items-center justify-end gap-2 border-t border-base-200 pt-4",
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = "CardFooter";
